import React, { useState } from 'react';
import { notificationService } from '../../services/notificationService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Send, Eye, Info, Shield, AlertTriangle, Clock, Users, Settings, Crown, User, UserCheck } from 'lucide-react';
import './SendNotification.scss';

const SendNotification = () => {
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        type: 'info',
        importance: 'medium',
        target_audience: 'all',
        specific_users: '',
        send_immediately: true,
        scheduled_time: ''
    });
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(false);
    const { t } = useLanguage();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim() || !formData.message.trim()) {
            alert(t('fillRequiredFields'));
            return;
        }

        setLoading(true);

        try {
            const notificationData = {
                ...formData,
                specific_users: formData.target_audience === 'specific' ?
                    JSON.stringify(formData.specific_users.split(',').map(id => id.trim())) :
                    null,
                scheduled_time: formData.send_immediately ? null : formData.scheduled_time
            };

            const response = await notificationService.sendSystemNotification(notificationData);

            if (response.success) {
                alert(t('notificationSendSuccess'));
                // Formu temizle
                setFormData({
                    title: '',
                    message: '',
                    type: 'info',
                    importance: 'medium',
                    target_audience: 'all',
                    specific_users: '',
                    send_immediately: true,
                    scheduled_time: ''
                });
                setPreview(false);
            }
        } catch (error) {
            console.error(t('notificationSendError'), error);
            alert(error.response?.data?.message || t('notificationSendError'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'security': return <Shield size={18} />;
            case 'warning': return <AlertTriangle size={18} />;
            case 'update': return <Clock size={18} />;
            case 'system': return <Info size={18} />;
            default: return <Info size={18} />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'security': return '#e74c3c';
            case 'warning': return '#f39c12';
            case 'update': return '#9b59b6';
            case 'system': return '#2ecc71';
            default: return '#3498db';
        }
    };

    const estimateRecipients = () => {
        switch (formData.target_audience) {
            case 'all':
                return t('allUsersCount');
            case 'premium':
                return t('premiumUsersCount');
            case 'free':
                return t('freeUsersCount');
            case 'specific':
                const count = formData.specific_users ? formData.specific_users.split(',').filter(id => id.trim()).length : 0;
                return `${count} ${t('specificUsersCount')}`;
            default:
                return '';
        }
    };

    return (
        <div className="send-notification">
            <div className="page-header">
                <h1>{t('sendNotification')}</h1>
                <p>{t('sendNotificationDescription')}</p>
            </div>

            <div className="send-notification__layout">
                <form className="send-notification__form" onSubmit={handleSubmit}>
                    {/* Bildirim İçeriği */}
                    <div className="send-notification__section">
                        <h3>
                            <Info size={20} />
                            {t('notificationContent')}
                        </h3>

                        <div className="send-notification__group">
                            <label>{t('notificationTitle')} *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                placeholder={t('enterNotificationTitle')}
                                maxLength={255}
                                required
                            />
                            <div className="send-notification__char-count">
                                {formData.title.length}/255
                            </div>
                        </div>

                        <div className="send-notification__group">
                            <label>{t('notificationMessage')} *</label>
                            <textarea
                                value={formData.message}
                                onChange={(e) => handleInputChange('message', e.target.value)}
                                placeholder={t('enterNotificationMessage')}
                                rows="5"
                                maxLength={1000}
                                required
                            />
                            <div className="send-notification__char-count">
                                {formData.message.length}/1000
                            </div>
                        </div>
                    </div>

                    {/* Bildirim Ayarları */}
                    <div className="send-notification__section">
                        <h3>
                            <Settings size={20} />
                            {t('notificationSettings')}
                        </h3>

                        <div className="send-notification__row">
                            <div className="send-notification__group">
                                <label>{t('notificationType')}</label>
                                <div className="send-notification__type-selector">
                                    {[
                                        { value: 'info', label: t('info'), icon: <Info size={16} /> },
                                        { value: 'warning', label: t('warning'), icon: <AlertTriangle size={16} /> },
                                        { value: 'security', label: t('security'), icon: <Shield size={16} /> },
                                        { value: 'update', label: t('update'), icon: <Clock size={16} /> },
                                        { value: 'system', label: t('system'), icon: <Info size={16} /> }
                                    ].map(type => (
                                        <label key={type.value} className="send-notification__option">
                                            <input
                                                type="radio"
                                                value={type.value}
                                                checked={formData.type === type.value}
                                                onChange={(e) => handleInputChange('type', e.target.value)}
                                            />
                                            <span className="send-notification__option-content">
                                                {type.icon}
                                                {type.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="send-notification__group">
                                <label>{t('importanceLevel')}</label>
                                <select
                                    value={formData.importance}
                                    onChange={(e) => handleInputChange('importance', e.target.value)}
                                >
                                    <option value="low">{t('low')}</option>
                                    <option value="medium">{t('medium')}</option>
                                    <option value="high">{t('high')}</option>
                                    <option value="critical">{t('critical')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="send-notification__group">
                            <label>{t('targetAudience')}</label>
                            <div className="send-notification__audience-selector">
                                {[
                                    { value: 'all', label: t('allUsers'), icon: <Users size={16} /> },
                                    { value: 'premium', label: t('premiumUsers'), icon: <Crown size={16} /> },
                                    { value: 'free', label: t('freeUsers'), icon: <User size={16} /> },
                                    { value: 'specific', label: t('specificUsers'), icon: <UserCheck size={16} /> }
                                ].map(audience => (
                                    <label key={audience.value} className="send-notification__option">
                                        <input
                                            type="radio"
                                            value={audience.value}
                                            checked={formData.target_audience === audience.value}
                                            onChange={(e) => handleInputChange('target_audience', e.target.value)}
                                        />
                                        <span className="send-notification__option-content">
                                            {audience.icon}
                                            {audience.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {formData.target_audience === 'specific' && (
                            <div className="send-notification__group">
                                <label>{t('specificUserIds')}</label>
                                <input
                                    type="text"
                                    value={formData.specific_users}
                                    onChange={(e) => handleInputChange('specific_users', e.target.value)}
                                    placeholder="123, 456, 789"
                                />
                                <small>{t('specificUsersHelp')}</small>
                            </div>
                        )}

                        <div className="send-notification__group">
                            <label className="send-notification__checkbox">
                                <input
                                    type="checkbox"
                                    checked={formData.send_immediately}
                                    onChange={(e) => handleInputChange('send_immediately', e.target.checked)}
                                />
                                <span className="checkmark"></span>
                                {t('sendImmediately')}
                            </label>
                        </div>

                        {!formData.send_immediately && (
                            <div className="send-notification__group">
                                <label>{t('scheduleTime')}</label>
                                <input
                                    type="datetime-local"
                                    value={formData.scheduled_time}
                                    onChange={(e) => handleInputChange('scheduled_time', e.target.value)}
                                    min={new Date().toISOString().slice(0, 16)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Özet ve Gönderim */}
                    <div className="send-notification__section">
                        <h3>{t('summary')}</h3>
                        <div className="send-notification__summary">
                            <div className="send-notification__summary-item">
                                <span className="send-notification__summary-label">{t('recipients')}:</span>
                                <span className="send-notification__summary-value">{estimateRecipients()}</span>
                            </div>
                            <div className="send-notification__summary-item">
                                <span className="send-notification__summary-label">{t('type')}:</span>
                                <span className="send-notification__summary-value">
                                    <span className="send-notification__type-indicator" style={{ backgroundColor: getTypeColor(formData.type) }}>
                                        {getTypeIcon(formData.type)}
                                        {t(formData.type)}
                                    </span>
                                </span>
                            </div>
                            <div className="send-notification__summary-item">
                                <span className="send-notification__summary-label">{t('importance')}:</span>
                                <span className={`send-notification__summary-value send-notification__importance--${formData.importance}`}>
                                    {t(formData.importance)}
                                </span>
                            </div>
                            <div className="send-notification__summary-item">
                                <span className="send-notification__summary-label">{t('sendingTime')}:</span>
                                <span className="send-notification__summary-value">
                                    {formData.send_immediately ? t('immediately') :
                                        new Date(formData.scheduled_time).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="send-notification__actions">
                        <button
                            type="button"
                            className="send-notification__preview"
                            onClick={() => setPreview(!preview)}
                            disabled={!formData.title || !formData.message}
                        >
                            <Eye size={16} />
                            {preview ? t('hidePreview') : t('preview')}
                        </button>

                        <button
                            type="submit"
                            className="send-notification__send"
                            disabled={loading || !formData.title || !formData.message}
                        >
                            <Send size={16} />
                            {loading ? t('sending') : t('sendNotification')}
                        </button>
                    </div>
                </form>

                {/* Önizleme Paneli */}
                {preview && (
                    <div className="send-notification__preview-panel">
                        <h3>{t('preview')}</h3>
                        <p className="send-notification__preview-description">{t('previewDescription')}</p>

                        <div className={`send-notification__preview send-notification__preview--${formData.type}`}>
                            <div className="send-notification__preview-header">
                                <span className="send-notification__preview-icon" style={{ color: getTypeColor(formData.type) }}>
                                    {getTypeIcon(formData.type)}
                                </span>
                                <span className="send-notification__preview-title">{formData.title}</span>
                                <span className={`send-notification__importance-badge send-notification__importance-badge--${formData.importance}`}>
                                    {t(formData.importance)}
                                </span>
                            </div>
                            <div className="send-notification__preview-message">
                                {formData.message}
                            </div>
                            <div className="send-notification__preview-footer">
                                <span className="send-notification__preview-time">{t('justNow')}</span>
                                <span className="send-notification__preview-audience">
                                    {t('to')} {estimateRecipients()}
                                </span>
                            </div>
                        </div>

                        <div className="send-notification__tips">
                            <h4>{t('tips')}</h4>
                            <ul>
                                <li>{t('tipClearLanguage')}</li>
                                <li>{t('tipImportantInfo')}</li>
                                <li>{t('tipAppropriateType')}</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SendNotification;